import React from 'react'
import ReactDOMServer from 'react-dom/server'
import { connect } from 'react-redux'
import showContextMenu from 'helpers/showContextMenu'
import RectangleEventExtensions from 'components/Markup/RectangleEventExtensions'
import LineLengthOverlay from 'components/Markup/LineLengthOverlay'
import mappings from 'components/Markup/mappings'
import style from './style'
import injectSheet from 'react-jss'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ContextMenu from 'ContextMenu'
import typeSpecificOptions from 'helpers/markups/typeSpecificOptions'
import toLatLngLiteral from 'helpers/map/toLatLngLiteral'
import setLocation from 'helpers/markups/setLocation'
import { DEFAULT_SHAPE_OPTIONS, INNER_WALL_OPTIONS, COPY_ICON_STACK } from 'helpers/globals'
import { addMarkupAction, updateMarkupAction, deleteMarkupAction, setCurrentMarkupAction } from 'actions/markupActions'
import { messageAction } from 'actions/messageActions'
import LabelOverlay from './LabelOverlay'
import determineLocationOfNewLabel from 'helpers/map/determineLocationOfNewLabel'
import { labelsSelector } from 'selectors/components'

const { addListener } = google.maps.event

const mapStateToProps = (state, { id, type }) => {
  const { cameras, currentEntity, ui: { drawingWall, drawingWire, isImperial } } = state

  return {
    currentEntityType: currentEntity.type,
    activeInUI: currentEntity.type === type && currentEntity.id === id,
    labels: labelsSelector(state),
    drawing: drawingWall || drawingWire,
    isImperial
  }
}

class Markup extends React.Component {
  constructor(props) {
    super(props)
    this.state = Object.assign({}, this.props, mappings[props.type])
    this.overlay = props.overlay
    this.contextMenu = new ContextMenu(props.map, this.contextMenuOptions())

    this.eventHandler = this.eventHandler.bind(this)
    this.remove = this.remove.bind(this)
  }

  componentDidMount() {
    const { remove, props, props: { id, updateMarkupAction, activeInUI }, state: { Overlay, title } } = this
    // get options for the type of overlay
    const options = typeSpecificOptions({ type: this.props.type, options: Object.assign({}, DEFAULT_SHAPE_OPTIONS, props, { editable: activeInUI }) })
    // only create overlay if reference is not created before
    if (!this.overlay) this.overlay = new Overlay(options)
    // set that overlay on the map
    this.overlay.setMap(this.props.map)
    setLocation(this.overlay, this.props.location)
    // lock the sucker if its locked
    this.setOverlayLocked(this.props.locked)
    this.addGenericEventListeners()
    // add remove function to state so changing/deleting projects work
    updateMarkupAction(id, { overlay: this.overlay, remove })

    if (typeof this[`add${title}EventListeners`] === 'function') {
      this[`add${title}EventListeners`]()
    }
  }

  componentWillUpdate(nextProps) {
    const { locked } = this.props
    if (locked !== nextProps.locked) this.setOverlayLocked(!locked)
    if (nextProps.strokeColor !== this.overlay.strokeColor && !this.innerWall) this.overlay.setOptions({ strokeColor: nextProps.strokeColor })
    if (this.innerWall && nextProps.strokeColor !== this.innerWall.strokeColor) this.innerWall.setOptions({ strokeColor: nextProps.strokeColor })
    if (nextProps.fillColor !== this.overlay.fillColor) this.overlay.setOptions({ fillColor: nextProps.fillColor })
    if (nextProps.isImperial !== this.props.isImperial) this.lineLengthOverlay && this.lineLengthOverlay.setIsImperial(nextProps.isImperial)
    if (nextProps.activeInUI && !nextProps.locked) {
      this.overlay.setEditable(true)
      google.maps.event.trigger(this.overlay, 'selected')
    } else {
      this.overlay.setEditable(false)
      google.maps.event.trigger(this.overlay, 'deselected')
    }
  }

  setOverlayLocked(locked) {
    this.overlay.setDraggable(!locked)
    this.overlay.setEditable(!locked)
  }

  addGenericEventListeners() {
    const { eventHandler, overlay, contextMenu, props: { messageAction } } = this

    overlay.addListener('contextmenu', showContextMenu(contextMenu))
    overlay.addListener('mousedown', this.setCurrentIfApplicable)
    addListener(contextMenu, 'menu_item_selected', eventHandler)
    overlay.addListener('mousedown', _ => this.props.locked && messageAction('generalError', 'This markup is locked. Right click to unlock.'))
  }

  setCurrentIfApplicable = _ =>
    !this.props.drawing && !this.props.activeInUI && this.props.setCurrentMarkupAction(this.props.id, this.props.type)

  addCircleEventListeners() {
    const { overlay, props: { id, updateMarkupAction } } = this

    addListener(
      overlay,
      'dragend',
      _ => updateMarkupAction(id, { location: toLatLngLiteral(overlay.getCenter()) })
    )

    addListener(
      overlay,
      'radius_changed',
      _ => updateMarkupAction(id, { radius: overlay.getRadius() })
    )
  }

  addPolygonEventListeners() {
    const { overlay, props: { id, updateMarkupAction } } = this
    const update = _ => updateMarkupAction(id, { location: overlay.getPath().getArray().map(pt => toLatLngLiteral(pt)) })

    const pathListeners = _ => {
      addListener(overlay.getPath(), 'set_at', update)
      addListener(overlay.getPath(), 'insert_at', update)
      addListener(overlay.getPath(), 'remove_at', update)
    }

    pathListeners()

    addListener(overlay, 'dragstart', _ => google.maps.event.clearListeners(overlay.getPath()))

    addListener(overlay, 'dragend', _ => {
      pathListeners()
      update()
    })
  }

  addPolylineEventListeners() {
    const { overlay, props: { map, isImperial } } = this
    this.addPolygonEventListeners()
    this.lineLengthOverlay = new LineLengthOverlay({
      map: map,
      polyline: overlay,
      containerClassName: this.props.classes.LineLengthOverlay,
      isImperial
    })
  }

  addWallEventListeners() {
    const { overlay, props: { map, strokeColor } } = this

    this.addPolygonEventListeners()
    this.innerWall = new google.maps.Polyline({
      ...INNER_WALL_OPTIONS,
      strokeColor,
      path: overlay.getPath(),
      map: map
    })
  }

  addRectangleEventListeners() {
    const { overlay, props: { id, updateMarkupAction } } = this
    new RectangleEventExtensions(overlay)

    addListener(
      overlay,
      'dragend',
      _ => updateMarkupAction(id, { location: overlay.getBounds().toJSON() })
    )

    addListener(
      overlay,
      'resized',
      _ => updateMarkupAction(id, { location: overlay.getBounds().toJSON() })
    )
  }

  addLabelEventListeners() {
    const { overlay, props: { id, updateMarkupAction } } = this
    addListener(
      overlay,
      'location_changed',
      location => updateMarkupAction(id, { location: toLatLngLiteral(location) })
    )

    addListener(
      overlay,
      'dimensions_changed',
      dimensions => updateMarkupAction(id, { dimensions })
    )

    addListener(
      overlay,
      'text_changed',
      text => updateMarkupAction(id, { text })
    )
  }

  contextMenuOptions = _ => {
    const menuItems = this.props.mapContextMenuOptions.menuItems
    const { classes } = this.props
    const menuItemBuilder = function() {
      const { state: { title }, props: { locked } } = this
      const items = [
        {
          className: classes.contextMenuItem,
          eventName: 'delete',
          id: 'menu_item_delete',
          label: `${ReactDOMServer.renderToStaticMarkup(<FontAwesomeIcon icon={['far', 'trash-alt']} />)} Delete ${title}`
        },
        {
          className: classes.contextMenuItem,
          eventName: 'toggleLock',
          id: 'menu_item_toggle_lock',
          label: `${ReactDOMServer.renderToStaticMarkup(<FontAwesomeIcon icon={locked ? 'unlock' : 'lock'} />)} ${locked ? 'Unlock' : 'Lock'} ${title}`
        }
      ]
      if (title !== 'Label') return menuItems.concat(items)
      return menuItems.concat([
        {
          className: classes.contextMenuItem,
          eventName: 'duplicateLabel',
          id: 'menu_item_duplicate_label',
          label: `${ReactDOMServer.renderToStaticMarkup(COPY_ICON_STACK)} Duplicate Label`
        }
      ]).concat(items)
    }

    return {
      classNames: {
        menu: classes.contextMenu,
        menuSeparator: classes.contextMenuSeparator
      },
      menuItems: menuItemBuilder.bind(this)
    }
  }

  eventHandler(latLng, eventName) {
    const { state: { mapContextMenuEventHandler, title }, props: { id, type, text, locked, labels, map, addMarkupAction, updateMarkupAction, deleteMarkupAction } } = this
    const mapContextMenuEvents = ['addCamHere', 'centerMapHere', 'zoomToAllDevices']
    if (mapContextMenuEvents.includes(eventName)) {
      mapContextMenuEventHandler(latLng, eventName)
    } else {
      switch (eventName) {
        case 'delete':
          if (locked) {
            this.props.messageAction('generalError', `Cannot delete locked ${title}. Right click to unlock.`)
          } else if (type === "label") {
            if (window.confirm(`Are you sure you want to delete \"${text}\"?`)) {
              this.remove()
              deleteMarkupAction(id)
            }
          } else {
            this.remove()
            deleteMarkupAction(id)
          }

          break
        case 'toggleLock':
          updateMarkupAction(id, { locked: !locked })
          break
        case 'duplicateLabel':
          const overlay = new LabelOverlay({ dimensions: Object.assign({}, this.overlay.dimensions), location: determineLocationOfNewLabel({ map, labels }),
                                              enableEditMode: true, draggable: true, text: this.overlay.text,
                                              fillColor: this.overlay.fillColor, strokeColor: this.overlay.strokeColor })
          addMarkupAction('label', overlay, true)
          break
      }
    }
    this.contextMenu.hide()
  }

  remove() {
    this.overlay.setMap(null)
    this.lineLengthOverlay && this.lineLengthOverlay.setMap(null)
    this.innerWall && this.innerWall.setMap(null)
  }

  render() {
    return null
  }
}

export default connect(mapStateToProps, { addMarkupAction, updateMarkupAction, setCurrentMarkupAction, deleteMarkupAction, messageAction })(injectSheet(style)(Markup))



// WEBPACK FOOTER //
// ./src/components/Markup/index.js